import React, {
  useEffect,
  useRef,
  useState,
  memo,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { getGeoLocationLatLong } from "../../services/commonServices";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const removeMapLayer = (map, layerId) => {
  if (map.getLayer(layerId)) map.removeLayer(layerId);
};

const removeMapSource = (map, sourceId) => {
  if (map.getSource(sourceId)) map.removeSource(sourceId);
};

const fitPolygonBounds = (map, formattedCoordinates) => {
  const bounds = new mapboxgl.LngLatBounds();
  formattedCoordinates.forEach((coords) => {
    coords.forEach(([lng, lat]) => bounds.extend([lng, lat]));
  });
  if (!bounds.isEmpty()) {
    map.fitBounds(bounds, { padding: 50, duration: 1000 });
  }
};

const fitCircleBounds = (map, centerCoords, radiusM) => {
  const offset = radiusM / 111320;
  map.fitBounds(
    [
      [centerCoords[0] - offset, centerCoords[1] - offset],
      [centerCoords[0] + offset, centerCoords[1] + offset],
    ],
    { padding: 50, duration: 1000 },
  );
};

const formatPolygonCoordinates = (polygonCoordinates) =>
  polygonCoordinates.map((polygon) =>
    polygon[0].map((point) => [point.lng, point.lat]),
  );

const clearDrawLayers = (map) => {
  removeMapLayer(map, "draw-line-layer");
  removeMapSource(map, "draw-line");
  removeMapLayer(map, "drawn-polygon-fill");
  removeMapLayer(map, "drawn-polygon-outline");
  removeMapSource(map, "drawn-polygon");
};

const MAP_BUTTON_STYLE = {
  position: "absolute",
  top: "10px",
  right: "10px",
  padding: "8px 12px",
  backgroundColor: "#007bff",
  color: "#fff",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
  zIndex: 1,
};

const DRAW_HINT_STYLE = {
  position: "absolute",
  top: "10px",
  left: "50%",
  transform: "translateX(-50%)",
  padding: "8px 16px",
  backgroundColor: "rgba(0, 0, 0, 0.7)",
  color: "#fff",
  borderRadius: "4px",
  fontSize: "14px",
  zIndex: 1,
  pointerEvents: "none",
};

const MapBoxComponent = forwardRef(
  (
    {
      center: centerProp,
      zoom = 10,
      radiusMiles,
      height = "400px",
      width = "100%",
      onRadiusUpdate,
      handleItemSelection,
      polygonCoordinates,
      showPolygon = false,
      isEditable = false,
      getDetailedAddress = false,
      drawMode = false,
      onDrawComplete,
    },
    ref,
  ) => {
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const mapLoadedRef = useRef(false);
    const drawHandlersRef = useRef(null);
    const isDrawingRef = useRef(false);
    const drawCoordsRef = useRef([]);
    const [hasDrawnPolygon, setHasDrawnPolygon] = useState(false);

    const DEFAULT_CENTER = [-112.074, 33.4484]; // Phoenix, AZ
    const center = useMemo(() => {
      if (!centerProp || centerProp.length < 2) return DEFAULT_CENTER;
      const [lng, lat] = centerProp.map(Number);
      if (!lng || !lat || isNaN(lng) || isNaN(lat)) return DEFAULT_CENTER;
      return [lng, lat];
    }, [centerProp]);
    const numericCenter = center;
    const [currentCenter, setCurrentCenter] = useState(numericCenter);
    const radiusMeters = useMemo(
      () => (radiusMiles ? radiusMiles * 1609.34 : 0),
      [radiusMiles],
    );


    const propsRef = useRef();
    propsRef.current = {
      currentCenter,
      radiusMeters,
      radiusMiles,
      isEditable,
      getDetailedAddress,
      handleItemSelection,
      onRadiusUpdate,
      showPolygon,
      polygonCoordinates,
      zoom,
      drawMode,
      onDrawComplete,
    };

    useImperativeHandle(ref, () => ({
      canvas: () => mapRef.current?.getCanvas(),
      map: () => mapRef.current,
    }));

    const clearVisuals = (map) => {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      removeMapLayer(map, "circle-layer");
      removeMapSource(map, "circle");
      removeMapLayer(map, "polygon-layer");
      removeMapLayer(map, "polygon-outline-layer");
      removeMapSource(map, "polygon");
    };

    const addCircle = (map, centerCoords, radiusM) => {
      map.addSource("circle", {
        type: "geojson",
        data: {
          type: "Feature",
          geometry: { type: "Point", coordinates: centerCoords },
        },
      });

      map.addLayer({
        id: "circle-layer",
        type: "circle",
        source: "circle",
        paint: {
          "circle-radius": {
            stops: [
              [0, 0],
              [20, radiusM / 0.075],
            ],
            base: 2,
          },
          "circle-color": "rgba(0, 123, 255, 0.1)",
          "circle-stroke-color": "#007bff",
          "circle-stroke-width": 2,
        },
      });
    };

    const addPolygon = (map, polyCoords) => {
      const formatted = formatPolygonCoordinates(polyCoords);

      map.addSource("polygon", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: formatted.map((coordinates) => ({
            type: "Feature",
            geometry: { type: "Polygon", coordinates: [coordinates] },
          })),
        },
      });

      map.addLayer({
        id: "polygon-layer",
        type: "fill",
        source: "polygon",
        paint: {
          "fill-color": "rgba(0, 123, 255, 0.5)",
          "fill-opacity": 0.4,
        },
      });

      map.addLayer({
        id: "polygon-outline-layer",
        type: "line",
        source: "polygon",
        paint: {
          "line-color": "#007bff",
          "line-width": 2,
        },
      });

      fitPolygonBounds(map, formatted);
    };

    const addMarker = (map, centerCoords) => {
      const props = propsRef.current;
      const marker = new mapboxgl.Marker({ draggable: props.isEditable })
        .setLngLat(centerCoords)
        .addTo(map);

      markerRef.current = marker;

      if (props.isEditable) {
        marker.on("dragend", async () => {
          const lngLat = marker.getLngLat();
          const draggedCenter = [lngLat.lng, lngLat.lat];
          const currentProps = propsRef.current;

          setCurrentCenter(draggedCenter);

          const { addressObject } = await getGeoLocationLatLong(
            lngLat.lat,
            lngLat.lng,
            currentProps.getDetailedAddress,
          );
          currentProps.handleItemSelection(addressObject);
        });
      }

      if (props.radiusMiles) {
        addCircle(map, centerCoords, props.radiusMeters);
        fitCircleBounds(map, centerCoords, props.radiusMeters);
      }
    };

    const initializeDraw = (map) => {
      removeDraw(map);

      map.addSource("draw-line", {
        type: "geojson",
        data: {
          type: "Feature",
          geometry: { type: "LineString", coordinates: [] },
        },
      });

      map.addLayer({
        id: "draw-line-layer",
        type: "line",
        source: "draw-line",
        paint: {
          "line-color": "#007bff",
          "line-width": 2,
          "line-dasharray": [2, 2],
        },
      });

      const onMouseDown = (e) => {
        if (e.originalEvent.button !== 0) return;
        isDrawingRef.current = true;
        drawCoordsRef.current = [[e.lngLat.lng, e.lngLat.lat]];
        map.dragPan.disable();
      };

      const onMouseMove = (e) => {
        if (!isDrawingRef.current) return;
        drawCoordsRef.current.push([e.lngLat.lng, e.lngLat.lat]);
        map.getSource("draw-line")?.setData({
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: drawCoordsRef.current,
          },
        });
      };

      const onMouseUp = () => {
        if (!isDrawingRef.current) return;
        isDrawingRef.current = false;
        map.dragPan.enable();

        const coords = drawCoordsRef.current;
        if (coords.length < 3) return;

        const closedCoords = [...coords, coords[0]];

        removeMapLayer(map, "draw-line-layer");
        removeMapSource(map, "draw-line");

        map.addSource("drawn-polygon", {
          type: "geojson",
          data: {
            type: "Feature",
            geometry: { type: "Polygon", coordinates: [closedCoords] },
          },
        });

        map.addLayer({
          id: "drawn-polygon-fill",
          type: "fill",
          source: "drawn-polygon",
          paint: {
            "fill-color": "rgba(0, 123, 255, 0.3)",
            "fill-opacity": 0.5,
          },
        });

        map.addLayer({
          id: "drawn-polygon-outline",
          type: "line",
          source: "drawn-polygon",
          paint: {
            "line-color": "#007bff",
            "line-width": 2,
          },
        });

        const bounds = new mapboxgl.LngLatBounds();
        closedCoords.forEach(([lng, lat]) => bounds.extend([lng, lat]));
        if (!bounds.isEmpty()) {
          map.fitBounds(bounds, { padding: 50, duration: 500 });
        }

        map.off("mousedown", onMouseDown);
        map.off("mousemove", onMouseMove);
        map.off("mouseup", onMouseUp);
        drawHandlersRef.current = null;

        const formattedCoords = closedCoords.map(([lng, lat]) => ({
          lng,
          lat,
        }));
        setHasDrawnPolygon(true);
        propsRef.current.onDrawComplete?.(formattedCoords);
      };

      drawHandlersRef.current = { onMouseDown, onMouseMove, onMouseUp };
      map.on("mousedown", onMouseDown);
      map.on("mousemove", onMouseMove);
      map.on("mouseup", onMouseUp);

      map.getCanvas().style.cursor = "crosshair";
    };

    const removeDraw = (map) => {
      if (drawHandlersRef.current) {
        map.off("mousedown", drawHandlersRef.current.onMouseDown);
        map.off("mousemove", drawHandlersRef.current.onMouseMove);
        map.off("mouseup", drawHandlersRef.current.onMouseUp);
        drawHandlersRef.current = null;
      }
      isDrawingRef.current = false;
      drawCoordsRef.current = [];
      clearDrawLayers(map);
      map.getCanvas().style.cursor = "";
      if (!map.dragPan._enabled) {
        map.dragPan.enable();
      }
    };

    const handleClearDraw = () => {
      const map = mapRef.current;
      if (!map) return;

      clearDrawLayers(map);
      setHasDrawnPolygon(false);
      initializeDraw(map);
    };

    const updateMapContent = () => {
      const map = mapRef.current;
      if (!map || !mapLoadedRef.current || !map.isStyleLoaded()) return;

      const props = propsRef.current;

      if (props.drawMode) {
        clearVisuals(map);
        if (!drawHandlersRef.current && !hasDrawnPolygon) {
          initializeDraw(map);
        }
        return;
      }

      removeDraw(map);
      setHasDrawnPolygon(false);
      clearVisuals(map);

      if (props.showPolygon && props.polygonCoordinates?.length > 0) {
        addPolygon(map, props.polygonCoordinates);
      } else {
        addMarker(map, props.currentCenter);
        map.flyTo({ center: props.currentCenter, zoom: props.zoom });
      }

      if (props.onRadiusUpdate && props.radiusMiles) {
        props.onRadiusUpdate(props.radiusMiles);
      }
    };

    useEffect(() => {
      if (!mapContainerRef.current) return;

      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: "mapbox://styles/mapbox/streets-v11",
        center: numericCenter,
        zoom,
        attributionControl: false,
        preserveDrawingBuffer: true,
      });

      mapRef.current = map;

      map.on("load", () => {
        mapLoadedRef.current = true;
        updateMapContent();
      });

      return () => {
        mapLoadedRef.current = false;
        if (drawHandlersRef.current && map) {
          removeDraw(map);
        }
        mapRef.current = null;
        markerRef.current = null;
        map.remove();
      };
    }, []);

    useEffect(() => {
      updateMapContent();
    }, [currentCenter, radiusMiles, showPolygon, polygonCoordinates, drawMode]);

    useEffect(() => {
      const newCenter = center.map(Number);
      if (
        !showPolygon &&
        (newCenter[0] !== currentCenter[0] || newCenter[1] !== currentCenter[1])
      ) {
        setCurrentCenter(newCenter);
      }
    }, [center, showPolygon]);

    const handleReset = () => {
      const map = mapRef.current;
      if (!map) return;

      if (showPolygon && polygonCoordinates?.length > 0) {
        fitPolygonBounds(map, formatPolygonCoordinates(polygonCoordinates));
      } else if (radiusMiles) {
        fitCircleBounds(map, currentCenter, radiusMeters);
      } else {
        map.flyTo({ center: currentCenter, zoom });
      }
    };

    return (
      <div
        style={{ position: "relative", height, width, overflow: "hidden" }}
        id="mapboxId"
        className="h-100"
      >
        <div
          ref={mapContainerRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            height: "100%",
            width: "100%",
          }}
        ></div>

        {drawMode && !hasDrawnPolygon && (
          <div style={DRAW_HINT_STYLE}>Click and drag to draw your area</div>
        )}

        {drawMode && hasDrawnPolygon && (
          <button onClick={handleClearDraw} style={MAP_BUTTON_STYLE}>
            Clear & Redraw
          </button>
        )}

        {!drawMode && (
          <button
            onClick={handleReset}
            style={MAP_BUTTON_STYLE}
            id="mapboxResetButton"
          >
            Reset Map
          </button>
        )}
      </div>
    );
  },
);

export default memo(MapBoxComponent);
